import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import {
  Autocomplete,
  ComboboxItem,
  AutocompleteProps,
  Loader,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { Player } from "../../../../gql/graphql";

export type PlayerSearchSelection = {
  id: string;
  username: string;
  ownerId: string | null;
};

const SearchPlayerQuery = gql`
  query ($search: String!) {
    searchPlayer(username: $search) {
      id
      username
      ownerId
    }
  }
`;

export type PlayerSearchProps = Omit<
  AutocompleteProps,
  "data" | "onChange"
> & {
  onChange: (value: PlayerSearchSelection) => void;
  resetOnSelect?: boolean;
};

export const PlayerSearch = ({
  resetOnSelect,
  ...props
}: PlayerSearchProps) => {
  const [searchPlayer] = useLazyQuery<{ searchPlayer: Player[] }>(
    SearchPlayerQuery,
  );
  const [players, setPlayers] = useState<Player[]>([]);
  const [data, setData] = useState<ComboboxItem[]>([]);
  const [value, setValue] = useState("");
  const [debouncedValue] = useDebouncedValue(value, 500, { leading: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (debouncedValue) {
      setLoading(true);
      searchPlayer({
        variables: {
          search: debouncedValue,
        },
      })
        .then((response) => {
          const results = response.data?.searchPlayer ?? [];
          setPlayers(results);
          setData(
            results.map((p) => ({
              label: p.username,
              value: p.id,
            })),
          );
        })
        .finally(() => setLoading(false));
    }
  }, [debouncedValue]);

  return (
    <Autocomplete
      placeholder="Search player"
      filter={({ options }: any) => options}
      rightSection={loading && <Loader size="xs" />}
      {...props}
      data={data}
      onOptionSubmit={(submittedValue: string) => {
        const item = data?.find(
          (d: any) =>
            (typeof d === "string" ? d : d.value) === submittedValue,
        );
        const player = players.find((p) => p.id === submittedValue);
        const selection: PlayerSearchSelection = {
          id: item?.value ?? submittedValue,
          username: item?.label ?? submittedValue,
          ownerId: player?.ownerId ?? null,
        };
        setValue(resetOnSelect ? "" : selection.username);
        props.onChange(selection);
      }}
      value={value}
      onChange={(data) => {
        setValue(data);
      }}
    />
  );
};
